/**
 * Consent state for non-essential (analytics / marketing) technologies.
 *
 * The consent decision is stored in localStorage, which is strictly necessary
 * storage: without it we could not remember that someone said no.
 *
 * IMPORTANT — how to add analytics correctly:
 *  - Do NOT add a script tag to index.html or import an analytics SDK at module
 *    scope. That would load it before consent and defeat the whole mechanism.
 *  - Put the loader inside loadConsentedScripts() below. It is only ever called
 *    when consent === 'accepted'.
 *  - Anything loaded there must also be torn down by revokeConsentedScripts()
 *    so that changing the preference to "reject" is respected.
 */

export type ConsentChoice = 'accepted' | 'rejected'

const STORAGE_KEY = 'kittypot-cookie-choice'

export function getConsent(): ConsentChoice | null {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY)
    return value === 'accepted' || value === 'rejected' ? value : null
  } catch {
    return null
  }
}

export function setConsent(choice: ConsentChoice) {
  try {
    window.localStorage.setItem(STORAGE_KEY, choice)
  } catch {
    /* storage unavailable — the choice simply is not remembered */
  }
  applyConsent(choice)
}

/**
 * Applies a consent decision. Called on every page load for a stored decision,
 * and immediately when the visitor makes or changes a choice.
 */
export function applyConsent(choice: ConsentChoice | null) {
  if (choice === 'accepted') {
    loadConsentedScripts()
  } else {
    revokeConsentedScripts()
  }
}

/**
 * Loads non-essential scripts. Currently a deliberate no-op: KittyPot's website
 * runs no analytics or marketing technologies. Add them here and nowhere else.
 */
function loadConsentedScripts() {
  /* No non-essential technologies are used on this website. */
}

/**
 * Unloads / disables anything loadConsentedScripts() added, and clears any
 * cookies it set. Currently a no-op for the same reason.
 */
function revokeConsentedScripts() {
  /* No non-essential technologies are used on this website. */
}
